/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CustomFilterV2, EmptyState } from '../../../components/v2';
import {
  tableV2AreaSx,
  tableV2ContainerSx,
  tableV2MultilineGridSx,
} from '../../../components/v2/tableV2Styles';
import { findVeiculosOptions } from '../../../store/veiculo/actions';
import { getVeiculosOptions } from '../../../store/veiculo/reducer';
import TableService from '../../../services/TableService';
import ChartsRow from './ChartsRow';
import KpiStrip from './KpiStrip';
import VeiculoSummary from './VeiculoSummary';
import {
  EMPTY_REPORT,
  buildSearchFromFilter,
  filtersEqual,
  formatPeriodLabel,
  getDefaultFilter,
  getScopeLabel,
  hasFilterSearchParams,
  isFleetScope,
  isPeriodInvalid,
  normalizeBiResponse,
  normalizeFilterForApi,
  parseFilterFromSearch,
} from './constants';
import {
  formatTransactionDate,
  renderCategoriaCell,
  renderDescricaoCell,
  renderValorCell,
} from './tableCells';

const RelatorioVeiculoV2 = ({ history, location, veiculosOptions, findVeiculosOptions }) => {
  const [localFilter, setLocalFilter] = useState(() =>
    parseFilterFromSearch(location.search, getDefaultFilter()),
  );
  const [reportData, setReportData] = useState(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [activeCategoria, setActiveCategoria] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const skipUrlSyncRef = useRef(false);
  const localFilterRef = useRef(localFilter);
  localFilterRef.current = localFilter;

  const syncUrlFromFilter = useCallback(
    (filter, { replace = true } = {}) => {
      const search = buildSearchFromFilter(filter);
      const pathname = location.pathname;
      const nextUrl = search ? `${pathname}?${search}` : pathname;
      const currentUrl = location.search ? `${pathname}${location.search}` : pathname;
      if (nextUrl === currentUrl) return;

      skipUrlSyncRef.current = true;
      if (replace) {
        history.replace(nextUrl);
      } else {
        history.push(nextUrl);
      }
    },
    [history, location.pathname, location.search],
  );

  useEffect(() => {
    findVeiculosOptions(true);
  }, [findVeiculosOptions]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }

    if (!hasFilterSearchParams(location.search)) {
      const defaults = getDefaultFilter();
      syncUrlFromFilter(defaults);
      if (!filtersEqual(defaults, localFilterRef.current)) {
        setLocalFilter(defaults);
        setActiveCategoria(null);
        setReload((prev) => !prev);
      }
      return;
    }

    const fromUrl = parseFilterFromSearch(location.search, getDefaultFilter());

    if (!filtersEqual(fromUrl, localFilterRef.current)) {
      setLocalFilter(fromUrl);
      setActiveCategoria(null);
      setReload((prev) => !prev);
    }
  }, [location.search, syncUrlFromFilter]);

  const apiFilter = useMemo(
    () => normalizeFilterForApi(localFilter),
    [localFilter],
  );

  const veiculoPlacaFallback = useMemo(() => {
    if (!localFilter.veiculoId || !loading) return null;
    const match = (veiculosOptions || []).find((v) => v.id === localFilter.veiculoId);
    return match?.placa || null;
  }, [localFilter.veiculoId, veiculosOptions, loading]);

  const isFleetView = isFleetScope(localFilter);
  const showVeiculoSummary = Boolean(localFilter.veiculoId);

  const contextLabel = useMemo(() => {
    const placa = reportData.veiculo?.placa ?? veiculoPlacaFallback;
    const parts = [getScopeLabel(placa)];
    const periodo = formatPeriodLabel(localFilter.inicio, localFilter.fim);
    if (periodo) {
      parts.push(`Período: ${periodo}`);
    }
    return parts.join(' · ');
  }, [
    reportData.veiculo,
    veiculoPlacaFallback,
    localFilter.inicio,
    localFilter.fim,
  ]);

  const fetchReport = useCallback(async () => {
    if (!apiFilter) {
      setReportData(EMPTY_REPORT);
      setFetchError(false);
      return;
    }

    setLoading(true);
    setFetchError(false);
    try {
      const response = await TableService.request('reports/veiculo/bi', apiFilter);
      setReportData(normalizeBiResponse(response?.data));
    } catch {
      setReportData(EMPTY_REPORT);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [apiFilter, reload]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const filteredTransacoes = useMemo(() => {
    const rows = reportData.transacoes || [];
    if (!activeCategoria) return rows;
    return rows.filter((row) => row.categoria === activeCategoria);
  }, [reportData.transacoes, activeCategoria]);

  const tableRows = useMemo(
    () =>
      filteredTransacoes.map((row, index) => ({
        id: `${row.placa || row.veiculoPlaca || 'fleet'}-${row.data}-${row.categoria}-${index}`,
        ...row,
      })),
    [filteredTransacoes],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'data',
        headerName: 'Data',
        minWidth: 110,
        flex: 0.7,
        renderCell: (params) => formatTransactionDate(params.row.data),
      },
    ];

    if (isFleetView) {
      cols.push({
        field: 'placa',
        headerName: 'Placa',
        minWidth: 100,
        renderCell: (params) => params.row.placa || '—',
      });
    }

    cols.push(
      {
        field: 'categoria',
        headerName: 'Categoria',
        minWidth: 130,
        flex: 0.9,
        renderCell: (params) => renderCategoriaCell(params.row.categoria),
      },
      {
        field: 'descricao',
        headerName: 'Descrição',
        minWidth: 220,
        flex: 1.5,
        sortable: false,
        renderCell: (params) => renderDescricaoCell(params.row.descricao),
      },
      {
        field: 'valor',
        headerName: 'Valor',
        minWidth: 120,
        flex: 0.8,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => renderValorCell(params.row.valor),
      },
    );

    return cols;
  }, [isFleetView]);

  const onApplyFilter = (filterData) => {
    if (isPeriodInvalid(filterData.inicio, filterData.fim)) {
      setFilterError('A data de início deve ser anterior ou igual à data de fim.');
      return;
    }
    const nextFilter = {
      ...filterData,
      veiculoId: filterData.veiculoId || null,
    };
    setFilterError(null);
    setLocalFilter(nextFilter);
    syncUrlFromFilter(nextFilter);
    setActiveCategoria(null);
    setOpenFilter(false);
    setReload((prev) => !prev);
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box>
            <Typography variant="h4">Relatório de Custos</Typography>
            {contextLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {contextLabel}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        {fetchError && (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
            Não foi possível carregar os dados do relatório. Verifique o período
            {isFleetView ? '' : ' e o veículo'} e tente novamente.
          </Alert>
        )}

        <>
          <VeiculoSummary
            veiculo={reportData.veiculo}
            loading={loading}
            visible={showVeiculoSummary}
          />

          <KpiStrip
            kpis={reportData.kpis}
            loading={loading}
            activeCategoria={activeCategoria}
            onSelectCategoria={setActiveCategoria}
          />

          <ChartsRow
            evolucaoMensal={reportData.evolucaoMensal}
            distribuicao={reportData.distribuicao}
            totalGasto={reportData.kpis?.totalGasto ?? 0}
            loading={loading}
          />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
              <InfoOutlinedIcon fontSize="small" color="action" />
              <Typography variant="h6">Transações</Typography>
              {activeCategoria && (
                <Typography variant="body2" color="text.secondary">
                    — filtrando por {activeCategoria}
                </Typography>
              )}
            </Stack>

            {!loading && tableRows.length === 0 ? (
              <EmptyState
                title="Nenhuma transação encontrada"
                message={
                  activeCategoria
                    ? `Não há lançamentos na categoria "${activeCategoria}" no período selecionado.`
                    : isFleetView
                      ? 'Não há custos registrados na frota no período selecionado.'
                      : 'Não há custos registrados para este veículo no período selecionado.'
                }
                onClearFilters={
                  activeCategoria ? () => setActiveCategoria(null) : undefined
                }
              />
            ) : (
              <Box sx={tableV2ContainerSx}>
                <Box sx={{ ...tableV2AreaSx, minHeight: 420 }}>
                  <DataGrid
                    rows={tableRows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    }}
                    rowHeight={52}
                    getRowHeight={() => 'auto'}
                    localeText={{
                      ...ptBR.components.MuiDataGrid.defaultProps.localeText,
                      noRowsLabel: 'Nenhuma transação encontrada',
                    }}
                    sx={{
                      ...tableV2MultilineGridSx,
                      border: 0,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </>

        {filterError && (
          <Snackbar
            open
            autoHideDuration={6000}
            onClose={() => setFilterError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="warning" onClose={() => setFilterError(null)} sx={{ width: '100%' }}>
              {filterError}
            </Alert>
          </Snackbar>
        )}

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => {
            setOpenFilter(false);
            setFilterError(null);
          }}
          title="Filtros"
          fullWidth
          defaultFilter={getDefaultFilter()}
          appliedFilter={localFilter}
          confirmButtonText="Aplicar"
          fields={[
            { field: 'useDates', md: 3 },
            {
              field: 'useVeiculo',
              key: 'veiculoId',
              label: 'Veículo (opcional)',
              multiSelect: false,
              md: 12,
              xs: 12,
            },
          ]}
          onFilter={onApplyFilter}
        />
      </Stack>
    </Box>
  );
};

RelatorioVeiculoV2.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func, replace: PropTypes.func }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  veiculosOptions: PropTypes.array.isRequired,
  findVeiculosOptions: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  veiculosOptions: getVeiculosOptions(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ findVeiculosOptions }, dispatch);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RelatorioVeiculoV2),
);
